import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useCreateDynamicsUserMappingMutation,
	useDeleteDynamicsUserMappingMutation,
	useGetDynamicsOwnersQuery,
	useGetDynamicsUserMappingsQuery,
} from "@/pages/Private/redux/dynamics/dynamics.api";
import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { dynamicsOwnerPrivilegeWarning } from "../utils/dynamicsOwnerPrivilegeWarning";

interface LocalMapping {
	userId: number;
	msdynamicsOwnerId: string;
	msdynamicsOwnerName?: string;
}

/**
 * Signalist user ↔ Dataverse `systemuser` mapping. The note sync in particular depends on
 * it: an annotation with no owner is invisible in the Dynamics timeline, so the sync skips
 * the batch rather than writing one.
 */
export const DynamicsUserMappingTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.msdynamics.userMapping.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: sendersData } = useGetSendersUsersQuery();
	const { data: ownersData } = useGetDynamicsOwnersQuery();
	const { data: userMappingsData, isLoading: isLoadingMappings } =
		useGetDynamicsUserMappingsQuery();
	const [createUserMapping] = useCreateDynamicsUserMappingMutation();
	const [deleteUserMapping] = useDeleteDynamicsUserMappingMutation();

	const [mappings, setMappings] = useState<LocalMapping[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (userMappingsData) {
			setMappings(
				userMappingsData.map((mapping) => ({
					userId: mapping.userId,
					msdynamicsOwnerId: mapping.msdynamicsOwnerId,
					msdynamicsOwnerName: mapping.msdynamicsOwnerName ?? undefined,
				}))
			);
		}
	}, [userMappingsData]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(ownersData?.owners ?? []).map((owner) => ({
				id: owner.id,
				title: owner.name ? `${owner.name} (${owner.email})` : owner.email,
			})),
		[ownersData]
	);

	const handleOwnerChange = (userId: number, ownerId: string) => {
		const owner = ownersData?.owners?.find((candidate) => candidate.id === ownerId);

		setMappings((prev) => {
			const existing = prev.find((mapping) => mapping.userId === userId);

			if (existing) {
				return prev.map((mapping) =>
					mapping.userId === userId
						? { ...mapping, msdynamicsOwnerId: ownerId, msdynamicsOwnerName: owner?.name }
						: mapping
				);
			}

			return [...prev, { userId, msdynamicsOwnerId: ownerId, msdynamicsOwnerName: owner?.name }];
		});
	};

	const handleSave = async () => {
		try {
			setIsSaving(true);

			const serverMappings = userMappingsData ?? [];

			const toDelete = serverMappings.filter((server) => {
				const local = mappings.find((mapping) => mapping.userId === server.userId);

				return !local || local.msdynamicsOwnerId !== server.msdynamicsOwnerId;
			});

			const toCreate = mappings.filter((mapping) => {
				const server = serverMappings.find((candidate) => candidate.userId === mapping.userId);

				return !server || server.msdynamicsOwnerId !== mapping.msdynamicsOwnerId;
			});

			await Promise.all(toDelete.map((mapping) => deleteUserMapping(mapping.id).unwrap()));

			await Promise.all(
				toCreate.map((mapping) =>
					createUserMapping({
						userId: mapping.userId,
						msdynamicsOwnerId: mapping.msdynamicsOwnerId,
						msdynamicsOwnerName: mapping.msdynamicsOwnerName,
					}).unwrap()
				)
			);

			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		} finally {
			setIsSaving(false);
		}
	};

	const senders = sendersData?.data ?? [];

	if (isLoadingMappings) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex flex-col w-full gap-4">
			{renderMessageSnackbar()}
			<div className="text-sm text-text-meta mb-1">{ts("description")}</div>

			{senders.length === 0 ? (
				<div className="py-6 text-center text-text-meta">{ts("noUsers")}</div>
			) : (
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-4 py-2 border-b border-border-light font-medium text-sm text-text-meta">
						<div className="flex-1">{ts("signalistUser")}</div>
						<div className="flex-1">{ts("msdynamicsOwner")}</div>
					</div>

					{senders.map((user) => {
						const currentMapping = mappings.find((mapping) => mapping.userId === user.id);
						const missingPrivileges = dynamicsOwnerPrivilegeWarning(
							userMappingsData ?? [],
							user.id,
							currentMapping?.msdynamicsOwnerId
						);

						return (
							<div key={user.id} className="flex flex-col gap-1 py-2 border-b border-border-light">
								<div className="flex items-center gap-4">
									<div className="flex-1 text-sm text-text">
										{user.firstName} {user.lastName} ({user.email})
									</div>
									<div className="flex-1">
										<Dropdown
											data={ownerOptions}
											defaultValue={ownerOptions.find(
												(option) => option.id === currentMapping?.msdynamicsOwnerId
											)}
											floating={true}
											handleSelect={(value) => handleOwnerChange(user.id, value.id as string)}
											label=""
										/>
									</div>
								</div>
								{missingPrivileges && (
									<div
										className="text-xs text-text-warning bg-status-warning-light rounded px-2 py-1"
										data-test={`dynamics-owner-privilege-warning-${user.id}`}
									>
										{t("company.msdynamics.userMapping.missingPrivileges", {
											privileges: missingPrivileges.join(", "),
										})}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			<div className="flex justify-end mt-4">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					title={t("basics.save")}
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
